import CustomBlock from '@/components/common/custom-block/custom-block';
import InterventionActionField from '@/components/common/custom-fields/intervention-action-field';
import FilterBox from '@/components/common/filters/common/filter-box';
import CustomListStylesContainer from '@/components/common/list-container/custom-list-styles-container';
import ChangeInterventionStatusField from '@/components/interventions/change-intervention-status-field';
import InterventionsListActions from '@/components/interventions/interventions-list-actions';
import InterventionsListBulkActionsButtons from '@/components/interventions/interventions-list-bulk-actions-buttons';
import CreateIntervention from '@/components/interventions/create/create-intervention';
import AddPlannedDate from '@/components/interventions/edit/add-planned-date';
import useGetAvatar from '@/hooks/useGetAvatar';
import { composeUri } from '@/utils/compose';
import {
    AVATAR_GROUP_AUDIT_TYPE,
    AVATAR_GROUP_QUOTE_TYPE,
    AVATAR_PROPERTIES,
    AVATARS,
    GROUP_AVATAR_SITE_PREFIX,
    GROUP_AVATARS,
    INTERVENTION_CHANGE_STATUS_NODE_LIST_OBJECT,
    INTERVENTION_CREATED_FROM_AVATAR_PROPERTY,
    INTERVENTION_METADATA_AVATAR_TYPE_NAME,
    INTERVENTION_STATUS_AVATAR_PROPERTY,
    INTERVENTION_TYPE_NAME_VALUES,
    PLACES,
    PLANNED_DATE_AVATAR_PROPERTY,
    REALISEE_STATE_KEY,
    ROLE_BIG_DELETE_INTERVENTION,
    ROLE_BIG_PLANED_DATE_INTERVENTION,
    ROLE_BIG_READ_INTERVENTION,
    ROLE_BIG_UPDATE_INTERVENTION_STATUS,
    STATUS_NAME_VALUES,
} from '@/utils/CONST';
import tableRowSx from '@/utils/table-row-sx';
import { Avatar } from '@/utils/types';
import Box from '@mui/material/Box';
import { get, map, split } from 'lodash';
import moment from 'moment';
import { useMemo, useState } from 'react';
import {
    AutocompleteInput,
    ChipField,
    DatagridConfigurable,
    DateField,
    DateInput,
    FunctionField,
    Identifier,
    List,
    ReferenceArrayField,
    ReferenceInput,
    ReferenceOneField,
    SelectInput,
    SingleFieldList,
    TextField,
    TextInput,
    useAuthenticated,
    usePermissions,
    useRecordSelection,
    useTranslate,
    useUnselectAll,
} from 'react-admin';
import { Outlet, useParams } from 'react-router';

const InterventionsList = () => {
    useAuthenticated();

    const translate = useTranslate();
    const { placeId, siteId } = useParams();
    const [selectedAvatarsIds, { select, unselect }] = useRecordSelection({
        resource: AVATARS,
        storeKey: 'interventionsList',
    });
    const unselectAll = useUnselectAll(AVATARS);
    const [multiSelectionState, setMultiSelectionState] = useState<boolean>(false);
    const { permissions } = usePermissions();
    const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
    const [openPlannedDateDialog, setOpenPlannedDateDialog] = useState<boolean>(false);
    const [selectedInterventionId, setSelectedInterventionId] = useState<string>('');

    const { avatar, isLoading: isLoadingPlace } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, placeId),
        queryKey: `${PLACES}_${siteId}`,
    });

    const placeAlphaId: any = useMemo(() => get(avatar, 'alphaId'), [avatar]);

    const onRowClick: any = (id: Identifier) => {
        if (multiSelectionState) {
            if (selectedAvatarsIds.includes(id)) {
                unselect([id]);
            } else {
                select([...selectedAvatarsIds, id]);
            }
        } else {
            unselectAll();
            select([id]);
        }
    };

    moment.locale('fr', {
        months: 'janvier_février_mars_avril_mai_juin_juillet_août_septembre_octobre_novembre_décembre'.split('_'),
        monthsShort: 'janv._févr._mars_avr._mai_juin_juil._août_sept._oct._nov._déc.'.split('_'),
        monthsParseExact: true,
        weekdays: 'dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi'.split('_'),
        weekdaysShort: 'dim._lun._mar._mer._jeu._ven._sam.'.split('_'),
        weekdaysMin: 'Di_Lu_Ma_Me_Je_Ve_Sa'.split('_'),
        weekdaysParseExact: true,
        relativeTime: {
            future: 'dans %s',
            past: 'il y a %s',
            s: 'quelques secondes',
            m: 'une minute',
            mm: '%d minutes',
            h: 'une heure',
            hh: '%d heures',
            d: 'un jour',
            dd: '%d jours',
            M: 'un mois',
            MM: '%d mois',
            y: 'un an',
            yy: '%d ans',
        },
    });

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 1,
                gridTemplateRows: '1fr',
                height: '100%',
                overflowY: 'auto',
            }}
        >
            <CustomBlock label={translate('main.navigation.intervention')} loading={isLoadingPlace}>
                <CustomListStylesContainer>
                    <List
                        actions={
                            <InterventionsListActions
                                multiSelectionState={multiSelectionState}
                                setMultiSelectionState={setMultiSelectionState}
                                onOpenCreateDialog={() => setOpenCreateDialog(true)}
                            />
                        }
                        component='div'
                        resource={AVATARS}
                        filter={{
                            'metadataAvatarType.name': INTERVENTION_METADATA_AVATAR_TYPE_NAME,
                            parent: placeId,
                            serialization_groups: ['read_avatar_current_node', 'read_node'],
                            enabled: true,
                            ...(siteId && { 'groupAvatars.name': `${GROUP_AVATAR_SITE_PREFIX}${siteId}` }),
                        }}
                        empty={false}
                        storeKey='interventionsList'
                        filters={filters(placeAlphaId)}
                        sort={{
                            field: 'creationDate',
                            order: 'DESC',
                        }}
                        queryOptions={{
                            enabled:
                                permissions?.includes(ROLE_BIG_READ_INTERVENTION) && !isLoadingPlace && !!placeAlphaId,
                        }}
                    >
                        <DatagridConfigurable
                            isRowSelectable={(record: Avatar) => {
                                return (
                                    record?.currentNode?.configuration.idForNodeHumanReadable !==
                                    INTERVENTION_CHANGE_STATUS_NODE_LIST_OBJECT[REALISEE_STATE_KEY].id
                                );
                            }}
                            rowClick={onRowClick}
                            rowSx={tableRowSx(selectedAvatarsIds)}
                            bulkActionButtons={
                                permissions?.includes(ROLE_BIG_UPDATE_INTERVENTION_STATUS) ? (
                                    multiSelectionState ? (
                                        <InterventionsListBulkActionsButtons />
                                    ) : (
                                        false
                                    )
                                ) : (
                                    false
                                )
                            }
                        >
                            <FunctionField
                                render={({ alphaId }: { alphaId: string }) => split(alphaId, ':')[1]}
                                label={'intervention.list.number'}
                            />

                            <ReferenceOneField
                                reference={AVATAR_PROPERTIES}
                                target='avatar'
                                label='intervention.list.type'
                                filter={{ name: INTERVENTION_CREATED_FROM_AVATAR_PROPERTY }}
                            >
                                <FunctionField render={({ value }: any) => translate(`intervention.list.${value}`)} />
                            </ReferenceOneField>

                            {permissions?.includes(ROLE_BIG_UPDATE_INTERVENTION_STATUS) ? (
                                <FunctionField
                                    render={({ currentNode, id, groupAvatars }: any) => (
                                        <ChangeInterventionStatusField
                                            // handleChangeInterventionsToAnulee={handleChangeInterventionsToAnulee}
                                            currentNode={currentNode}
                                            id={id}
                                            groupAvatars={groupAvatars}
                                        />
                                    )}
                                    label='intervention.list.status'
                                />
                            ) : (
                                <TextField source='currentNode.name' label='intervention.list.status' />
                            )}

                            <DateField
                                sortable={false}
                                options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                                source={'currentNode.dateOfTakesPlace'}
                                label='intervention.list.status_date'
                            />

                            <DateField
                                sortable={false}
                                options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                                source={'creationDate'}
                                label={'intervention.list.creation_date'}
                            />

                            <ReferenceOneField
                                reference={AVATAR_PROPERTIES}
                                target='avatar'
                                label='intervention.list.planned_date'
                                filter={{ name: PLANNED_DATE_AVATAR_PROPERTY }}
                            >
                                <DateField
                                    options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                                    source={'value'}
                                />
                            </ReferenceOneField>

                            <ReferenceOneField
                                reference={AVATAR_PROPERTIES}
                                target='avatar'
                                label='intervention.list.start_date'
                                filter={{ name: INTERVENTION_STATUS_AVATAR_PROPERTY }}
                            >
                                <DateField
                                    options={{
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: 'numeric',
                                        minute: 'numeric',
                                        second: 'numeric',
                                    }}
                                    source={'customFields.start.when'}
                                />
                            </ReferenceOneField>

                            <ReferenceOneField
                                reference={AVATAR_PROPERTIES}
                                target='avatar'
                                label='intervention.list.end_date'
                                filter={{ name: INTERVENTION_STATUS_AVATAR_PROPERTY }}
                            >
                                <DateField
                                    options={{
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: 'numeric',
                                        minute: 'numeric',
                                        second: 'numeric',
                                        hour12: false,
                                    }}
                                    source={'customFields.end.when'}
                                />
                            </ReferenceOneField>

                            <ReferenceOneField
                                reference={AVATAR_PROPERTIES}
                                target='avatar'
                                label='intervention.list.duration'
                                filter={{ name: INTERVENTION_STATUS_AVATAR_PROPERTY }}
                            >
                                {/*TODO no poner nada aquí cuando esa variable es 0*/}
                                <FunctionField
                                    render={({ customFields }: any) =>
                                        customFields.time_passed > 0 &&
                                        moment()
                                            .locale('fr')
                                            .subtract(customFields.time_passed, 'minutes')
                                            .fromNow(true)
                                    }
                                />
                            </ReferenceOneField>

                            <ReferenceArrayField
                                sortable={false}
                                reference={GROUP_AVATARS}
                                source='groupAvatars'
                                label='disorder.list.present_devis'
                                sx={{
                                    '& > span > div': {
                                        display: 'flex',
                                        gap: 0.2,
                                    },
                                }}
                                filter={{ type: AVATAR_GROUP_QUOTE_TYPE }}
                            >
                                <SingleFieldList linkType={false}>
                                    <ChipField source='name' />
                                </SingleFieldList>
                            </ReferenceArrayField>

                            <ReferenceArrayField
                                sortable={false}
                                reference={GROUP_AVATARS}
                                source='groupAvatars'
                                label='disorder.list.present_audit'
                                sx={{
                                    '& > span > div': {
                                        display: 'flex',
                                        gap: 0.2,
                                    },
                                }}
                                filter={{ type: AVATAR_GROUP_AUDIT_TYPE }}
                            >
                                <SingleFieldList linkType={false}>
                                    <ChipField source='name' />
                                </SingleFieldList>
                            </ReferenceArrayField>

                            <FunctionField
                                render={(avatar: any) => (
                                    <InterventionActionField
                                        avatar={avatar}
                                        withDelete={permissions?.includes(ROLE_BIG_DELETE_INTERVENTION)}
                                        withChangeDate={permissions?.includes(ROLE_BIG_PLANED_DATE_INTERVENTION)}
                                        onOpenPlannedDateDialog={(interventionId) => {
                                            setSelectedInterventionId(interventionId);
                                            setOpenPlannedDateDialog(true);
                                        }}
                                    />
                                )}
                            />
                        </DatagridConfigurable>
                    </List>
                </CustomListStylesContainer>
            </CustomBlock>

            {openCreateDialog && (
                <CreateIntervention
                    open={openCreateDialog}
                    onClose={() => setOpenCreateDialog(false)}
                />
            )}

            {openPlannedDateDialog && selectedInterventionId && (
                <AddPlannedDate
                    open={openPlannedDateDialog}
                    onClose={() => {
                        setOpenPlannedDateDialog(false);
                        setSelectedInterventionId('');
                    }}
                    interventionId={selectedInterventionId}
                />
            )}

            <Outlet />
        </Box>
    );
};

const filters = (placeAlphaId: string | undefined) => [
    <FilterBox source={'intervention_filters1'} label={'intervention.filters.label'} alwaysOn>
        <SelectInput
            source={'properties.value'}
            name={'properties.value'}
            choices={map(INTERVENTION_TYPE_NAME_VALUES, (v) => ({ name: `intervention.list.${v}`, id: v }))}
            label='intervention.list.type'
            sx={{
                width: 200,
            }}
            alwaysOn
            resettable
        />

        <SelectInput
            source={'currentNode.name'}
            name={'currentNode.name'}
            choices={map(STATUS_NAME_VALUES, (v) => ({ name: v, id: v }))}
            label='intervention.filters.status'
            sx={{
                width: 200,
            }}
            alwaysOn
            resettable
        />
    </FilterBox>,

    <FilterBox source={'intervention_filters2'} label={'intervention.filters.period'} alwaysOn>
        <DateInput
            source='creationDate__after'
            name='creationDate__after'
            label='disorder.filters.from'
            sx={{
                width: 200,
            }}
            alwaysOn
        />

        <DateInput
            source='creationDate__before'
            name='creationDate__before'
            label='disorder.filters.to'
            sx={{
                width: 200,
            }}
            alwaysOn
        />
    </FilterBox>,

    <FilterBox source={'intervention_filters3'} label={'intervention.filters.label'} alwaysOn>
        <TextInput
            source={'alphaId'}
            parse={(v) => (!!v ? `I:${v}` : '')}
            format={(v) => split(v, ':')[1]}
            name={'alphaId'}
            label='intervention.filters.number'
            sx={{
                width: 200,
                mb: 0.6,
            }}
            alwaysOn
            resettable
        />

        <ReferenceInput
            source='groupAvatars.id'
            name='groupAvatars.id'
            reference={GROUP_AVATARS}
            filter={{
                type: AVATAR_GROUP_QUOTE_TYPE,
                identifiers: [placeAlphaId],
            }}
            queryOptions={{
                enabled: placeAlphaId !== undefined,
            }}
        >
            <AutocompleteInput optionText='name' size='small' label={'intervention.filters.quote'} />
        </ReferenceInput>

        <ReferenceInput
            source='groupAvatars.id'
            name='groupAvatars.id'
            reference={GROUP_AVATARS}
            filter={{
                type: AVATAR_GROUP_AUDIT_TYPE,
                identifiers: [placeAlphaId],
            }}
            queryOptions={{
                enabled: placeAlphaId !== undefined,
            }}
        >
            <AutocompleteInput optionText='name' size='small' label={'intervention.filters.audit'} />
        </ReferenceInput>
    </FilterBox>,
];

export default InterventionsList;
